import { useEffect, useState } from "react";
import { Link, getRouteApi } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { unwrap } from "@tern/api-client";
import { ArrowUpRight, CircleDot, Flag, ListChecks, LoaderCircle, Plus, Settings } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { ProjectIcon } from "@/components/ProjectIcon";
import { EmptyState } from "@/components/EmptyState";
import { Markdown, MarkdownEditor } from "@/components/Markdown";
import { Picker } from "@/components/Picker";
import { Avatar } from "@/components/PropertyIcons";
import { api, errorMessage, workspaceMembersQuery } from "@/lib/api";
import { isActive, isClosed } from "@/lib/issues";
import { daysBetween, nextMilestone, todayISO } from "@/lib/tasks";
import { liveTasks } from "@/lib/store";
import { liveIssues, useProject, useProjectStore, type ProjectData } from "@/lib/store";
import { toast } from "@/lib/toast";

const route = getRouteApi("/$ws/project/$key");

/** Ensure the project is in the store (bootstrap or delta). */
export function useLoadProject(ws: string, key: string) {
  const load = useProjectStore((s) => s.load);
  const status = useProjectStore((s) => s.status[`${ws}/${key}`]);
  useEffect(() => {
    if (status !== "ready" && status !== "loading") void load(ws, key);
  }, [ws, key, load, status]);
}

export function ProjectLoading({ ws, kkey }: { ws: string; kkey: string }) {
  const { t } = useTranslation();
  const status = useProjectStore((s) => s.status[`${ws}/${kkey}`]);
  const error = useProjectStore((s) => s.error[`${ws}/${kkey}`]);
  if (status === "error") {
    return (
      <div className="flex flex-1 items-center justify-center overflow-y-auto">
        <EmptyState title={t("project.notFound")} body={error ?? ""} actions={<Link to="/$ws/inbox" params={{ ws }} className="btn">{t("common.back")}</Link>} />
      </div>
    );
  }
  return <div className="flex flex-1 items-center justify-center gap-2.5 text-[13px] text-muted" role="status"><LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />{t("common.loading")}</div>;
}

export function ProjectHomePage() {
  const { t } = useTranslation();
  const { ws, key } = route.useParams();
  useLoadProject(ws, key);
  const d = useProject(ws, key);
  if (!d) return <ProjectLoading ws={ws} kkey={key} />;
  return (
    <>
      <PageHeader
        crumbs={[d.project.name, t("nav.home")]}
        right={
          d.project.role === "admin" ? (
            <Link to="/$ws/project/$key/settings" params={{ ws, key }} className="btn btn-ghost h-7 w-7 justify-center p-0" title={t("nav.settings")}>
              <Settings className="h-4 w-4" />
            </Link>
          ) : null
        }
      />
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[960px] px-5 py-7 sm:px-8 sm:py-10 lg:px-12">
          <div className="flex items-center gap-4">
            <ProjectIcon project={d.project} size={48} tile />
            <div className="min-w-0">
              <h1 className="break-words text-[24px] font-semibold leading-tight tracking-tight">{d.project.name}</h1>
              <div className="mt-1.5 text-[12px] text-muted">
                {d.project.key} · {t(`project.${d.project.visibility}`)}
              </div>
            </div>
            {!d.project.role && <JoinButton d={d} />}
          </div>

          <Summary d={d} />
          <Description d={d} />
          <Members d={d} />
        </div>
      </div>
    </>
  );
}

function Section({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="mt-8 border-t border-border pt-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-[14px] font-semibold text-fg">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Description({ d }: { d: ProjectData }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(d.project.description);
  const [editorBusy, setEditorBusy] = useState(false);
  const canEdit = !!d.project.role;
  const save = useMutation({
    mutationFn: async () => unwrap(await api.PATCH("/api/workspaces/{slug}/projects/{key}", { params: { path: { slug: d.slug, key: d.project.key } }, body: { description: draft } })),
    onSuccess: (p) => {
      useProjectStore.getState().applyProject(p);
      setEditing(false);
    },
    onError: (e) => toast(errorMessage(e), { tone: "error" }),
  });
  return (
    <Section title={t("project.description")} action={canEdit && !editing ? <button className="btn btn-ghost h-7 text-[12.5px]" onClick={() => { setDraft(d.project.description); setEditing(true); }}>{t("common.edit")}</button> : null}>
      {editing ? (
        <div>
          <MarkdownEditor onBusyChange={setEditorBusy} projectId={d.project.id} value={draft} onChange={setDraft} onSave={() => save.mutate()} disabled={save.isPending} autoFocus minRows={6} placeholder={t("project.descriptionPlaceholder")} />
          <div className="mt-2 flex gap-2">
            <button className="btn btn-primary h-8" onClick={() => save.mutate()} disabled={save.isPending || editorBusy}>{t("common.save")}</button>
            <button className="btn h-8" onClick={() => setEditing(false)}>{t("common.cancel")}</button>
          </div>
        </div>
      ) : d.project.description ? (
        <div className={canEdit ? "cursor-text rounded-md p-1 -m-1 hover:bg-hover/40" : ""} onClick={() => canEdit && (setDraft(d.project.description), setEditing(true))}>
          <Markdown source={d.project.description} />
        </div>
      ) : (
        <p className="rounded-lg border border-dashed border-border-strong px-4 py-5 text-[13px] leading-6 text-muted">{canEdit ? t("project.descriptionEmptyEditable") : t("project.descriptionEmpty")}</p>
      )}
    </Section>
  );
}

function Members({ d }: { d: ProjectData }) {
  const { t } = useTranslation();
  const isAdmin = d.project.role === "admin";
  const wsMembers = useQuery({ ...workspaceMembersQuery(d.slug), enabled: isAdmin });
  const candidates = (wsMembers.data ?? []).filter((m) => !d.members.some((pm) => pm.user_id === m.user_id));
  const add = useMutation({
    mutationFn: async (user_id: string) => unwrap(await api.POST("/api/workspaces/{slug}/projects/{key}/members", { params: { path: { slug: d.slug, key: d.project.key } }, body: { user_id, role: "member" } })),
    onSuccess: (members) => useProjectStore.getState().applyMembers(d.project.id, members),
    onError: (e) => toast(errorMessage(e), { tone: "error" }),
  });
  const setRole = useMutation({
    mutationFn: async (v: { user_id: string; role: "admin" | "member" }) => unwrap(await api.PATCH("/api/workspaces/{slug}/projects/{key}/members/{user_id}", { params: { path: { slug: d.slug, key: d.project.key, user_id: v.user_id } }, body: { role: v.role } })),
    onSuccess: (members) => useProjectStore.getState().applyMembers(d.project.id, members),
    onError: (e) => toast(errorMessage(e), { tone: "error" }),
  });
  const remove = useMutation({
    mutationFn: async (user_id: string) => unwrap(await api.DELETE("/api/workspaces/{slug}/projects/{key}/members/{user_id}", { params: { path: { slug: d.slug, key: d.project.key, user_id } } })),
    onSuccess: (members) => useProjectStore.getState().applyMembers(d.project.id, members),
    onError: (e) => toast(errorMessage(e), { tone: "error" }),
  });
  return (
    <Section
      title={`${t("workspace.members")} · ${d.members.length}`}
      action={
        isAdmin ? (
          <Picker
            value={null}
            items={candidates.map((m) => ({ id: m.user_id, label: m.name, icon: <Avatar user={m} size={16} />, keywords: m.email }))}
            onSelect={(id) => add.mutate(id)}
            placeholder={t("project.addMember")}
            trigger={<button className="btn btn-ghost h-7 text-[12.5px]"><Plus className="h-3.5 w-3.5" /> {t("project.addMember")}</button>}
          />
        ) : null
      }
    >
      <ul className="divide-y divide-border rounded-lg border border-border">
        {d.members.map((m) => (
          <li key={m.user_id} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
            <Avatar user={m} size={26} />
            <div className="min-w-[100px] flex-1">
              <div className="truncate text-[13px] font-medium">{m.name}</div>
              <div className="mt-0.5 break-all text-[12px] text-muted">{m.email}</div>
            </div>
            {isAdmin ? (
              <select className="input h-8 w-[104px] py-0" aria-label={t("project.memberRole", { name: m.name })} value={m.role} onChange={(e) => setRole.mutate({ user_id: m.user_id, role: e.target.value as "admin" | "member" })}>
                <option value="member">{t("workspace.role.member")}</option>
                <option value="admin">{t("workspace.role.admin")}</option>
              </select>
            ) : (
              <span className="text-[12px] text-muted">{t(`workspace.role.${m.role}`)}</span>
            )}
            {isAdmin && (
              <button className="btn btn-ghost h-8 text-[12px] text-muted hover:text-danger" aria-label={t("project.removeMember", { name: m.name })} onClick={() => remove.mutate(m.user_id)}>
                {t("common.remove")}
              </button>
            )}
          </li>
        ))}
      </ul>
    </Section>
  );
}

function Summary({ d }: { d: ProjectData }) {
  const { t } = useTranslation();
  const issues = liveIssues(d);
  const open = issues.filter((i) => !isClosed(d, i)).length;
  const inProgress = issues.filter((i) => isActive(d, i) && d.states[i.state_id]?.type === "started").length;
  const nm = nextMilestone(d);
  return (
    <Section title={t("project.summary")}>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Tile d={d} label={t("project.openIssues")} value={open} to={{ tab: "active" }} />
        <Tile d={d} label={t("project.inProgress")} value={inProgress} to={{ tab: "active" }} />
        <Tile d={d} label={t("project.inProgressTasks")} value={liveTasks(d).filter((x) => x.status === "started").length} to={{ tab: "started", kind: "tasks" }} />
        <Tile d={d} label={t("project.nextMilestone")} value={nm ? <span className="block text-[14px] leading-5">{nm.name}<span className="mt-1 block text-[12px] font-normal text-muted">D{daysBetween(todayISO(), nm.target_date) >= 0 ? "-" : "+"}{Math.abs(daysBetween(todayISO(), nm.target_date))}</span></span> : <span className="text-[13px] font-normal text-faint">—</span>} to={{ kind: "milestones" }} />
      </div>
    </Section>
  );
}

function Tile({ d, label, value, to }: { d: ProjectData; label: string; value: React.ReactNode; to: { tab?: string; kind?: "tasks" | "milestones" } | null }) {
  const inner = (
    <>
      <div className="mb-3 flex items-center justify-between text-muted" aria-hidden="true">
        {to?.kind === "milestones" ? <Flag className="h-4 w-4" /> : to?.kind === "tasks" ? <ListChecks className="h-4 w-4" /> : <CircleDot className="h-4 w-4" />}
        {to && <ArrowUpRight className="h-3.5 w-3.5 text-faint transition-colors group-hover:text-accent" />}
      </div>
      <div className="text-[12px] font-medium text-muted">{label}</div>
      <div className="mt-2 break-words text-[27px] font-semibold leading-tight tracking-tight tabular-nums">{value}</div>
    </>
  );
  const cls = "group min-w-0 rounded-lg border border-border bg-sidebar/40 p-4 transition-colors hover:border-border-strong hover:bg-hover";
  if (!to) return <div className="min-w-0 rounded-lg border border-border p-4">{inner}</div>;
  if (to.kind === "milestones") return <Link to="/$ws/project/$key/milestones" params={{ ws: d.slug, key: d.project.key }} className={cls}>{inner}</Link>;
  if (to.kind === "tasks") return <Link to="/$ws/project/$key/tasks/$tab" params={{ ws: d.slug, key: d.project.key, tab: to.tab ?? "all" }} className={cls}>{inner}</Link>;
  return <Link to="/$ws/project/$key/issues/$tab" params={{ ws: d.slug, key: d.project.key, tab: to.tab ?? "all" }} className={cls}>{inner}</Link>;
}

function JoinButton({ d }: { d: ProjectData }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const join = useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/workspaces/{slug}/projects/{key}/join", { params: { path: { slug: d.slug, key: d.project.key } } })),
    onSuccess: async () => {
      await useProjectStore.getState().load(d.slug, d.project.key, { force: true });
      await qc.invalidateQueries({ queryKey: ["projects", d.slug] });
    },
  });
  return (
    <button className="btn btn-primary ml-auto h-8" onClick={() => join.mutate()} disabled={join.isPending}>
      {t("project.join")}
    </button>
  );
}
